/**
 * A native select in the `Input` box, with a chevron. Put it inside a `Field`.
 *
 * Native, because the phone's own picker is the best list a phone has, and a custom listbox would
 * have to rebuild type-ahead and screen reader support to be as good. A long option is cut with an
 * ellipsis inside the box rather than widening the page: the select has no minimum width of its own.
 *
 * `placeholder` adds an empty first option ("Choose" by default) for a select that starts with no
 * answer. A select that always holds a value leaves it out.
 */
import * as stylex from "@stylexjs/stylex";
import { useId } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { controlStyle } from "./controlStyle.ts";
import { focusRing } from "./focus.ts";
import { ChevronDownIcon } from "./icons.tsx";
import { textStyle } from "./textStyle.ts";
const styles = {
  reason: {
    kogj98: "x1ghz6dp",
    kMwMTN: "x1lm7cez",
    $$css: true
  },
  select: {
    kysU6D: "xjyslct",
    kwRFfy: "x5wz70f",
    kg5iWk: "xlyipyv",
    khDVqt: "xuxw1ft",
    kVQacm: "xb3r6kr",
    kkrTdU: "x1ypdohk",
    $$css: true
  }
};
export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}
export interface SelectProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly SelectOption[];
  readonly id?: string | undefined;
  /** The empty first option's words, for a select that starts with no answer. */
  readonly placeholder?: string;
  readonly size?: "md" | "lg";
  readonly name?: string;
  readonly autoComplete?: string;
  readonly required?: boolean | undefined;
  readonly "aria-required"?: true | undefined;
  readonly disabled?: boolean;
  /** Why it cannot be changed, shown under it and read as its description while it is disabled. */
  readonly disabledReason?: string;
  readonly "aria-describedby"?: string | undefined;
  readonly "aria-invalid"?: true | undefined;
  readonly "aria-label"?: string;
  readonly testId?: string;
}
export function Select({
  value,
  onChange,
  options,
  placeholder,
  size = "md",
  disabled = false,
  disabledReason,
  testId,
  "aria-invalid": invalid,
  "aria-describedby": describedBy,
  ...rest
}: SelectProps) {
  const reasonId = useId();
  const reason = disabled ? disabledReason : undefined;
  const described = [describedBy, reason === undefined ? undefined : reasonId].filter(Boolean).join(" ");
  return <span className="x78zum5 xdt5ytf xskm3c9 xh8yej3 xeuugli">
      <span className="x1n2onr6 x78zum5 xh8yej3 xeuugli">
        <select {...rest} aria-describedby={described === "" ? undefined : described} value={value} disabled={disabled} aria-invalid={invalid} onChange={event => onChange(event.currentTarget.value)} data-testid={testId} {...stylex.props(controlStyle.box, controlStyle[size], styles.select, invalid === true && controlStyle.invalid, disabled && controlStyle.disabled, focusRing)}>
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map(option => <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>)}
        </select>
        <span aria-hidden="true" className="x10l6tqk x10no89f xmzullg x78zum5 x6s0dn4 x1lm7cez x47corl">
          <ChevronDownIcon />
        </span>
      </span>
      {reason !== undefined && <span id={reasonId} {...stylex.props(textStyle.label, styles.reason)}>
          {reason}
        </span>}
    </span>;
}